import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import {
    Courier,
    CourierDetailsDto,
    CourierEditForm,
    initCourierForm,
    updateCourierData,
} from '../../../api/couriers';
import { DictionaryDto, useContentApi, useDictionaryApi } from '../../../api/dictionary';

export function useCourierActivate() {
    const [courierFormOpen, setCourierFormOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [courierForm, setCourierForm] = useState<CourierEditForm>(initCourierForm());
    const [documentTypes, setDocumentTypes] = useState<DictionaryDto[]>([]);
    const { fetchAll: fetchDocumentTypes } = useContentApi<DictionaryDto>('/document-type/list');
    const { getById } = useDictionaryApi<CourierDetailsDto>('/courier/detail');
    const [courierId, setCourierId] = useState<number | string>(0);
    const [courierLoading, setCourierLoading] = useState(false);

    const courierFormValidScheme = Yup.object().shape({
        document_type: Yup.object().shape({
            id: Yup.string().required('Выберите вид документа'),
        }),
        passport_number: Yup.string().required('Введите номер документа'),
        passport_date: Yup.string().required('Введите дату выдачи').nullable(),
        passport_issued_by: Yup.string().required('Введите кем выдано'),
        card_number: Yup.string().required('Введите номер счета IBAN'),
        fact_address: Yup.string().required('Введите фактический адрес'),
    });

    const handleUpdate = useCallback(
        async (values: CourierEditForm, setSubmitting: (val: boolean) => void) => {
            try {
                await updateCourierData(courierId, values);
                setCourierFormOpen(false);
                enqueueSnackbar('Данные курьера успешно обновлены', { variant: 'success' });
            } catch (e) {
                enqueueSnackbar(`Ошибка при сохранении данных ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            } finally {
                setSubmitting(false);
            }
        },
        [courierId, enqueueSnackbar]
    );

    const loadDocumentTypes = useCallback(async () => {
        if (courierFormOpen && documentTypes.length === 0) {
            try {
                setDocumentTypes(await fetchDocumentTypes());
            } catch (e) {
                enqueueSnackbar(`Ошибка при получении данных ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            }
        }
    }, [courierFormOpen, documentTypes.length, enqueueSnackbar, fetchDocumentTypes]);

    const handleEditButton = useCallback(
        async (courier: Courier | undefined, id?: number | string) => {
            setCourierLoading(true);
            setCourierFormOpen(true);

            let localCourier: Courier | undefined = courier ? { ...courier } : undefined;
            if (!courier) {
                if (id) {
                    const res = await getById(id.toString());
                    localCourier = { ...res.courier };
                } else {
                    return;
                }
            }

            setCourierId(localCourier?.id as number);

            const data = initCourierForm(localCourier);
            setCourierForm(data);
            setCourierLoading(false);
        },
        [getById]
    );

    useEffect(() => {
        loadDocumentTypes();
    }, [documentTypes.length, loadDocumentTypes]);

    return {
        courierForm,
        courierFormValidScheme,
        setCourierForm,
        handleUpdate,
        courierFormOpen,
        setCourierFormOpen,
        documentTypes,
        handleEditButton,
        courierLoading,
    };
}
